import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react'
import {
  Flex,
  Pagination,
  Loader,
  Center,
  Title,
  Button,
  Stack,
  Text
} from '@mantine/core'
import { IconHeart } from '@tabler/icons-react'
import { BooksContext } from '../../../contexts/BooksContext'
import { setScrollPostion } from '../../../utils'

import BookCard from '../../../components/BookCard'
import { useBookDetails } from '../../../contexts/BookDetailsContext'

import classes from './Favorites.module.css'
import { useQueryClient } from '@tanstack/react-query'

const FAVORITES_QUERY_KEY = 'favorites'
const PARSED_FAVORITES_KEY = 'parsed-favorites'

export default function FavoritesPage() {
  const [page, setPage] = useState(1)
  const [books, setBooks] = useState([])
  const [pages, setPages] = useState(0)
  const [, setTotalItems] = useState(0)
  const [localLoading, setLocalLoading] = useState(false)
  const { setBook: setSelectionBook } = useBookDetails()

  const {
    parseFavorites,
    loading: contextLoading,
    setLoading: setContextLoading
  } = useContext(BooksContext)
  const queryClient = useQueryClient()
  const removedBooks = useRef(new Set())

  const fetchFavoriteBooks = useCallback(
    async (forceRefresh = false) => {
      setLocalLoading(true)
      try {
        if (forceRefresh) {
          queryClient.removeQueries([PARSED_FAVORITES_KEY, page])
        }

        setContextLoading(true)

        const data = await parseFavorites(page)

        if (!data || !Array.isArray(data.realItems)) {
          setBooks([])
          setPages(0)
          setTotalItems(0)
          return
        }

        const filteredBooks = data.realItems.filter((book) => {
          if (!book || !book.uuid) return false
          return !removedBooks.current.has(book.uuid)
        })

        const booksPerPage = 12
        const realTotalItems = data.totalElements - removedBooks.current.size

        const realTotalPages = Math.max(
          1,
          Math.ceil(realTotalItems / booksPerPage)
        )

        if (page > realTotalPages && realTotalPages > 0 && page !== 1) {
          setPage(realTotalPages)
          return
        }

        setBooks(filteredBooks)
        setPages(realTotalPages)
        setTotalItems(realTotalItems)
      } catch (error) {
        console.error('Error fetching favorites:', error)
        setBooks([])
        setPages(0)
        setTotalItems(0)
      } finally {
        setLocalLoading(false)
      }
    },
    [parseFavorites, page, queryClient, setContextLoading]
  )

  const changePage = useCallback(
    (newPage) => {
      if (newPage !== page) {
        setPage(newPage)
        setScrollPostion(0, 0)
      }
    },
    [page]
  )

  const onClickDetails = (book) => {
    setSelectionBook(book)
  }

  const handleFavoriteChange = useCallback(
    (wasFavorited, bookData) => {
      const bookUuid = bookData?.uuid
      if (!wasFavorited && bookUuid) {
        removedBooks.current.add(bookUuid)

        setBooks((prevBooks) => prevBooks.filter((b) => b.uuid !== bookUuid))

        setTotalItems((prevTotal) => {
          const newTotal = Math.max(0, prevTotal - 1)

          const booksPerPage = 12
          const newPages = Math.max(1, Math.ceil(newTotal / booksPerPage))

          if (newPages !== pages) {
            setPages(newPages)
          }

          if (books.length === 1 && page > 1 && newPages < page) {
            setTimeout(() => setPage(page - 1), 0)
          }

          return newTotal
        })

        queryClient.invalidateQueries([FAVORITES_QUERY_KEY])

        queryClient.invalidateQueries({
          predicate: (query) => {
            return (
              Array.isArray(query.queryKey) &&
              query.queryKey[0] === PARSED_FAVORITES_KEY
            )
          }
        })
      }
    },
    [page, books.length, pages, queryClient]
  )

  useEffect(() => {
    fetchFavoriteBooks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const isLoading = localLoading || contextLoading

  return (
    <>
      <Title order={1} className={classes.title}>
        Favoritos
      </Title>

      {isLoading && (
        <Center maw={400} h={100} mx="auto">
          <Loader size="lg" />
        </Center>
      )}

      {!isLoading && books.length === 0 && (
        <Center>
          <Stack>
            <Center mb={20} mt={20}>
              <div className={classes.emptyResultIconContainer}>
                <IconHeart size={28} className={classes.emptyResultIcon} />
              </div>
            </Center>

            <Center>
              <Text className={classes.emptyResultTitle}>
                Sua lista de favoritos está esperando!
              </Text>
            </Center>

            <Text className={classes.emptyResultSubtitle}>
              Clique no ícone para adicionar seus livros preferidos.
            </Text>

            <Center>
              <Button
                mt={20}
                component="a"
                rel="noopener noreferrer"
                variant="white"
                color="dark"
                className={classes.emptyResultButton}
                href={`/books`}
              >
                Ver catálogo
              </Button>
            </Center>
          </Stack>
        </Center>
      )}

      {!isLoading && books.length > 0 && (
        <>
          <Flex
            mih={50}
            gap="md"
            justify="flex-start"
            align="flex-start"
            direction="row"
            wrap="wrap"
            rowGap={30}
            columnGap={20.7}
          >
            {books.map((book) => (
              <div key={book.uuid}>
                <BookCard
                  book={book}
                  runAfterOnClickFavorite={() =>
                    handleFavoriteChange(false, book)
                  }
                  onClickDetails={() => onClickDetails(book)}
                />
              </div>
            ))}
          </Flex>

          {pages > 1 && (
            <Pagination
              value={page}
              total={pages}
              onChange={changePage}
              position="center"
              align="center"
              size="lg"
              className={classes.pagination}
            />
          )}
        </>
      )}
    </>
  )
}
